/** Locale formatting of the audit dashboard (dates in the browser time zone, exact instant on demand) */
export class AuditFormat {
  readonly #dateTime: Intl.DateTimeFormat;
  readonly #date: Intl.DateTimeFormat;
  readonly #exact: Intl.DateTimeFormat;
  readonly #relative: Intl.RelativeTimeFormat;
  readonly #number: Intl.NumberFormat;
  readonly timeZone: string;

  constructor(locale: string) {
    const loc = AuditFormat.locale(locale);
    this.#dateTime = new Intl.DateTimeFormat(loc, { dateStyle: 'short', timeStyle: 'medium' });
    this.#date = new Intl.DateTimeFormat(loc, { dateStyle: 'medium' });
    this.#exact = new Intl.DateTimeFormat(loc, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3,
      timeZoneName: 'short',
    });
    this.#relative = new Intl.RelativeTimeFormat(loc, { numeric: 'auto' });
    this.#number = new Intl.NumberFormat(loc);
    this.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  /** "fr_FR" -> "fr-FR"; invalid tags fall back to the browser locale */
  static locale(tag: string | null | undefined): string | undefined {
    const t = (tag ?? '').replace('_', '-');
    try {
      return t ? Intl.getCanonicalLocales(t)[0] : undefined;
    } catch {
      return undefined;
    }
  }

  dateTime(iso: string | null | undefined): string {
    const d = AuditFormat.parse(iso);
    return d ? this.#dateTime.format(d) : '';
  }

  date(iso: string | null | undefined): string {
    const d = AuditFormat.parse(iso);
    return d ? this.#date.format(d) : '';
  }

  /** full instant with milliseconds and time zone (tooltips, copy) */
  exact(iso: string | null | undefined): string {
    const d = AuditFormat.parse(iso);
    return d ? `${this.#exact.format(d)} (${iso})` : '';
  }

  relative(iso: string | null | undefined, now: Date): string {
    const d = AuditFormat.parse(iso);
    if (!d) return '';
    const s = Math.round((d.getTime() - now.getTime()) / 1000);
    const abs = Math.abs(s);
    if (abs < 45) return this.#relative.format(s, 'second');
    if (abs < 2700) return this.#relative.format(Math.round(s / 60), 'minute');
    if (abs < 79_200) return this.#relative.format(Math.round(s / 3600), 'hour');
    if (abs < 2_592_000) return this.#relative.format(Math.round(s / 86_400), 'day');
    return this.#date.format(d);
  }

  number(n: number | null | undefined): string {
    return n === null || n === undefined ? '' : this.#number.format(n);
  }

  static parse(iso: string | null | undefined): Date | null {
    if (!iso) return null;
    const t = Date.parse(iso);
    return isNaN(t) ? null : new Date(t);
  }
}
